/**
 * VIB-2979: grenene i tilbudsarbeidsrommet (docs/tilbudsarbeidsrom/00-kjoreplan.md §1).
 *
 * Grenene er faner på samme tilbud. Ingen gren krever at en annen er ferdig,
 * så statusen er bare informasjon. Budsjett er dagens tilbudsside og ligger
 * på `/tilbud/:id`, slik at eksisterende lenker virker som før.
 */

export type WorkspaceBranch = "grunnlag" | "ide" | "budsjett" | "presentasjon" | "tilbud";

export type BranchStatus = "tom" | "pabegynt" | "klar" | "kommer";

export interface WorkspaceBranchDef {
  key: WorkspaceBranch;
  label: string;
  icon: string;
}

export const WORKSPACE_BRANCHES: readonly WorkspaceBranchDef[] = [
  { key: "grunnlag", label: "Grunnlag", icon: "user-round" },
  { key: "ide", label: "Idé", icon: "lightbulb" },
  { key: "budsjett", label: "Budsjett", icon: "table" },
  { key: "presentasjon", label: "Presentasjon", icon: "presentation" },
  { key: "tilbud", label: "Tilbud", icon: "file-text" },
];

export const BRANCH_STATUS_LABEL: Record<BranchStatus, string> = {
  tom: "Tom",
  pabegynt: "Påbegynt",
  klar: "Klar",
  kommer: "Kommer",
};

/** Grener som ennå ikke er bygget (fase 3–5). */
export const UPCOMING_BRANCHES: readonly WorkspaceBranch[] = ["presentasjon", "tilbud"];

/** URL-segmentet `:gren`. Mangler det eller er ukjent, vises Budsjett. */
export function parseBranch(param: string | undefined): WorkspaceBranch {
  const match = WORKSPACE_BRANCHES.find((b) => b.key === param);
  return match ? match.key : "budsjett";
}

export function branchPath(offerId: string, branch: WorkspaceBranch): string {
  return branch === "budsjett" ? `/tilbud/${offerId}` : `/tilbud/${offerId}/${branch}`;
}

export interface GrunnlagFields {
  description: string | null | undefined;
  targetAudience: string | null | undefined;
  budgetFrameNok: number | null | undefined;
}

const filled = (v: string | null | undefined) => !!v && v.trim().length > 0;

/** Klar når beskrivelse, målgruppe og ramme er fylt ut; påbegynt når noe er det. */
export function grunnlagStatus(f: GrunnlagFields): BranchStatus {
  const parts = [filled(f.description), filled(f.targetAudience), f.budgetFrameNok != null];
  if (parts.every(Boolean)) return "klar";
  return parts.some(Boolean) ? "pabegynt" : "tom";
}

/** Klar når tilbudet er sendt eller akseptert; påbegynt når det har linjer. */
export function budsjettStatus(lineCount: number, offerStatus: string | null | undefined): BranchStatus {
  if (lineCount > 0 && (offerStatus === "sent" || offerStatus === "accepted")) return "klar";
  return lineCount > 0 ? "pabegynt" : "tom";
}

/** Klar når minst ett kort er brukt i budsjettet; påbegynt når tavlen har kort. */
export function ideStatus(cardCount: number, usedInBudgetCount: number): BranchStatus {
  if (cardCount === 0) return "tom";
  return usedInBudgetCount > 0 ? "klar" : "pabegynt";
}
